import './tabMap.css'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'
import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, useMap, Polygon, Popup, useMapEvents, Marker } from 'react-leaflet';
import { Button, ButtonGroup, Card, Container, Row, Col } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../ToastContext/ToastContext';

L.Icon.Default.mergeOptions({ // imposta l'icona del marker
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// funzione per aggiungere una label sulla mappa
function MapLabel({ position, children, labelThreshold }) { // il valore threshold è riferito allo zoom
    const map = useMap(); // mappa corrente dal contesto di React-Leaflet
    map.doubleClickZoom.disable(); // disabilita lo zoom con il doppio click
    const [container, setContainer] = useState(null); // stato locale al componente funzione per salvare il riferimento al div della label
    const [showLabel, setShowLabel] = useState(map.getZoom() >= labelThreshold); /* definisce uno stato, per mostrare la label, con valore di
                                                                                    default pari a true se è verificata la condizione per cui
                                                                                    lo zoom attuale della mappa appena caricata è maggiore o
                                                                                    uguale del valore threshold */

    useEffect(() => {
        const pane = map.getPane('labelsPane') || map.createPane('labelsPane'); // recupera o crea un nuovo pane chiamato "labelsPane"
        /* in Leaflet, un pane è un livello HTML interno alla mappa che serve a organizzare e sovrapporre gli elementi visivi; ogni pane ha uno
        z-index in modo da determinare l'ordine di visualizzazione rispetto agli altri pane */
        const div = L.DomUtil.create('div', 'custom-label'); // crea un div DOM dove verrà visualizzata la label, con nome classe "custom-label"
        pane.appendChild(div); // aggiunge il div al pane
        setContainer(div); // salva il riferimento nello stato "container" per usarlo nel rendering

        const updatePosition = () => { // funzione che aggiorna la posizione del div sulla mappa
            const point = map.latLngToLayerPoint(position); // converte coordinate geografiche in coordinate pixel
            L.DomUtil.setPosition(div, point); // posiziona il div nei pixel calcolati prima
        };

        map.on('zoomend viewreset move', updatePosition); // registra eventi che aggiornano la vista e quindi la posizione del div
        updatePosition(); // aggiorna subito la posizone del div

        return () => { // rimuove il div e i listener quando il componente viene smontato
            map.off('zoomend viewreset move', updatePosition);
            pane.removeChild(div);
        };
    }, [map, position]); // riesegue se cambiano map o position

    useMapEvents({ // hook fornito da React-Leaflet che permette di ascoltare eventi della mappa (ad es. zoom, click, movimento...)
        zoomend: () => { /* è un evento nativo di Leaflet che viene emesso quando l’animazione di zoom termina: qui è utile per sapere se il livello 
                            di zoom è cambiato e agire di conseguenza */
            const zoom = map.getZoom(); // recupera lo zoom attuale della mappa
            setShowLabel(zoom >= labelThreshold); // imposta lo stato showLabel a true se lo zoom è maggiore o uguale del valore threshold
        }
    });

    return showLabel && (container ? ReactDOM.createPortal(children, container) : null); /* usa React Portal per renderizzare il contenuto (children) 
                                                                                            React dentro il div (in overlay sulla mappa) Leaflet 
                                                                                            creato (se showLabel è true);
                                                                                            quindi il portal viene usato per iniettare un elemento 
                                                                                            HTML direttamente dentro un pane di Leaflet */
}

// definizione di una icona custom per le parcel monitorate
var infoParcelIcon = L.icon({
    iconUrl: 'geo-fill.svg', // dalla cartella public

    iconSize: [25, 82], // dimensioni dell'icona
    iconAnchor: [12.5, 47], // punto dell'icona a cui corrisponde la posizione del marker
    popupAnchor: [0, -27] // punto dal quale il popup si apre rispetto a iconAnchor
});

// definizione di una icona custom per la parcel monitorata attiva (click icona)
var infoParcelIconActive = L.icon({
    iconUrl: 'geo-fill-red.svg', // dalla cartella public

    iconSize: [25, 82], // dimensioni dell'icona
    iconAnchor: [12.5, 47], // punto dell'icona a cui corrisponde la posizione del marker
    popupAnchor: [0, -27] // punto dal quale il popup si apre rispetto a iconAnchor
});

export default function TabMap({ parcels, onSaveLayer }) {
    const [showAttribution, setShowAttribution] = useState(false); // stato per mostrare o non mostrare le attribution
    const [polygons, setPolygons] = useState([]); // stato array dei poligoni sulla mappa
    const selectedId = useRef(null); // stato per memorizzare l'id del layer selezionato
    const [showToolbox, setShowToolbox] = useState(false); // stato per mostrare la toolbox del singolo poligono
    const activeLayer = useRef(null); // stato per memorizzare il layer attivo
    const [showRemoveButton, setShowRemoveButton] = useState(false);
    const [showMonitoring, setShowMonitoring] = useState(false);
    const [clickedInfo, setClickedInfo] = useState(null); // indica il click sull'icona info della parcel
    const [isSaved, setIsSaved] = useState(false);
    const editSession = useRef(null);
    const [showUploadModal, setShowUploadModal] = useState(false); // stato per mostrare modal di upload
    const [enableUploadButton, setEnableUploadButton] = useState(true); // stato per abilitare/disabilitare il tasto upload geojson
    const isSavedRef = useRef(false);
    const setSaved = (v) => { isSavedRef.current = v; setIsSaved(v); };
    const { t } = useTranslation(['usersetting', 'common', 'error', 'tabupload']);
    const { showToast } = useToast();

    function markModified(layer) {
        layer.options.modified = true;
        layer.setStyle({ color: 'yellow' });
        layer.pm?.setOptions?.({ pathOptions: { color: 'yellow' } });
    }

    // crea un layer "ombra" copiando il layer base
    function makeShadowFrom(layer) {
        const latlngs = layer.getLatLngs();
        const shadow = L.polygon(latlngs, { color: 'green', weight: 3 });
        shadow._isShadow = true;

        // *** COPIA PROPERTIES dal layer base ***
        if (layer.feature) {
            // clone “safe” delle feature/properties
            shadow.feature = JSON.parse(JSON.stringify(layer.feature));
        } else {
            shadow.feature = { type: 'Feature', properties: {} };
        }

        const onChanged = () => markModified(shadow);
        shadow.on('pm:edit pm:update pm:dragend pm:rotateend pm:cut', onChanged);

        return shadow;
    }






    function cloneLatLngs(latlngs) {
        if (!Array.isArray(latlngs)) return latlngs;
        return latlngs.map(part => (Array.isArray(part) ? cloneLatLngs(part) : L.latLng(part.lat, part.lng)));
    }




    function beginEditSession(layer, layerId) {
        editSession.current = {
            layer,
            layerId,
            originalLatLngs: cloneLatLngs(layer.getLatLngs()),
            wasNew: !!layer._isNew,
        };
        selectedId.current = layerId;
        activeLayer.current = layer;

        // Collega gli handler di "modificato" una sola volta per layer
        if (!layer._hasModifyHook) {
            const onChanged = () => markModified(layer);
            // copri i casi principali di Geoman che avevamo nella toolbox 
            layer.on('pm:edit', onChanged);
            layer.on('pm:update', onChanged);
            layer.on('pm:dragend', onChanged);
            layer.on('pm:rotateend', onChanged);
            layer.on('pm:cut', onChanged);
            layer._hasModifyHook = true;
        }
    }



    function abandonIfPending(map) {
        const s = editSession.current;
        if (!s) return;

        const savedNow = isSavedRef.current;

        if (savedNow) {
            // SALVATO CON SUCCESSO
            if (!s.wasNew) {
                // EDIT-EXISTING: rimuovi il layer editato per evitare il doppione
                try { map.removeLayer(s.layer); } catch { }
            }
            // CREATE: non toccare il layer; lo toglierà l'effetto su parcels
        } else {
            // ANNULLATO / CHIUSO SENZA SALVARE
            if (s.wasNew) {
                try { map.removeLayer(s.layer); } catch { }
            } else if (s.layer?.options?.modified) {
                try {
                    s.layer.setLatLngs(s.originalLatLngs);
                    s.layer.setStyle({ color: '#3388ff' });
                    s.layer.options.modified = false;
                    s.layer.pm?.disable?.();
                    s.layer.pm?.disableLayerDrag?.();
                    s.layer.pm?.disableRotate?.();
                } catch { }
            }
        }

        // reset UI & stato sessione
        setShowToolbox(false);
        handleRemoveButton(false);
        setSaved(false); // reset anche il ref
        selectedId.current = null;
        activeLayer.current = null;
        editSession.current = null;

        try {
            map.pm?.disableGlobalCutMode?.();
            map.pm?.disableDraw?.('Polygon');
        } catch { }
    }

    function ClickAwayCanceller({ enabled }) {
        const map = useMap();
        const suppressNextClick = React.useRef(false);

        // dopo pm:create ignoriamo il primo click che segue
        useEffect(() => {
            if (!map?.pm) return;

            const onCreate = () => { suppressNextClick.current = true; };
            map.on('pm:create', onCreate);

            return () => {
                map.off('pm:create', onCreate);
            };
        }, [map]);

        useMapEvents({
            click(e) {
                if (!enabled) return;

                // 1) se sto disegnando con Geoman, non annullare
                if (map?.pm?.globalDrawModeEnabled?.()) return;

                // 2) ignora il click immediatamente successivo a pm:create
                if (suppressNextClick.current) {
                    suppressNextClick.current = false;
                    return;
                }

                const t = e.originalEvent?.target;
                const isVector = t?.closest?.('.leaflet-interactive');
                const isControl = t?.closest?.('.leaflet-control');
                const isToolbox = t?.closest?.('.polygon-toolbox-button-group');
                if (isVector || isControl || isToolbox) return;

                abandonIfPending(map);
            }
        });

        return null;
    }

    // --- OPEN/CLOSE SESSION

    function abandonEdit(map) {
        const s = editSession.current;
        if (!s) return;

        // ripristina il base se esiste
        try {
            if (s.baseLayer && s.baseLayer._prevStyleForEdit) {
                s.baseLayer.setStyle(s.baseLayer._prevStyleForEdit);
                delete s.baseLayer._prevStyleForEdit;
            }
        } catch { }

        try {
            s.shadow.pm?.disable?.();
            map.removeLayer(s.shadow);   // rimuovi solo l’ombra
        } catch { }

        editSession.current = null;
        activeLayer.current = null;
        selectedId.current = null;

        setShowToolbox(false);
        handleRemoveButton(false);

        try {
            map.pm?.disableGlobalCutMode?.();
            map.pm?.disableDraw?.('Polygon');
        } catch { }
    }


    function selectPolygon(map, layer, layerId) {
        map.fitBounds(layer.getBounds());
        // chiudi eventuale sessione pendente
        if (editSession.current) abandonEdit(map);

        let shadow;
        let wasNew = !!layer._isNew; // true per pm:create/upload

        if (wasNew) {
            shadow = layer;
            shadow.setStyle({ color: 'green', weight: 3, opacity: 1, fillOpacity: 0.25 });
            shadow.bringToFront?.();
        } else {
            // *** OFFUSCA il base durante l’editing ***
            // salva lo stile corrente per poterlo ripristinare
            layer._prevStyleForEdit = {
                color: layer.options.color,
                weight: layer.options.weight,
                opacity: layer.options.opacity ?? 1,
                fillOpacity: layer.options.fillOpacity ?? 0.2,
            };
            // “spegni” il base (resta in mappa ma invisibile)
            layer.setStyle({ opacity: 0, fillOpacity: 0 });

            // crea la shadow sopra
            shadow = makeShadowFrom(layer).addTo(map);
        }

        try {
            map.eachLayer(l => { if (l.pm && l !== shadow) l.pm.disable(); });
            shadow.pm?.enable?.();
            shadow.bringToFront?.();            // <-- assicurati che sia sopra
            map.pm?.disableDraw?.('Polygon');
        } catch { }

        // --- handler per riaprire la toolbox se fosse chiusa
        if (!shadow._reopenToolboxHook) {
            shadow.on('click', () => {
                activeLayer.current = shadow;
                selectedId.current = layerId;
                setShowToolbox(true);
                try { shadow.pm?.enable?.(); } catch { }
            });
            shadow._reopenToolboxHook = true;
        }

        editSession.current = {
            baseLayer: wasNew ? null : layer,
            shadow,
            wasNew,
        };

        activeLayer.current = shadow;
        selectedId.current = layerId;

        setShowToolbox(true);
        handleRemoveButton(!!(layer._drawnByGeoman || layer._isNew));
    }

    /*
    function selectPolygon(map, layer, layerId) {
      // se stai ricliccando lo stesso layer, non toccare la sessione
      if (selectedId.current === layerId && editSession.current) {
        // solo UI: disabilita altri pm, chiudi draw, apri toolbox
        map.eachLayer(l => { if (l.pm && !layer.pm.enabled()) l.pm.disable(); });
        try { map.pm.disableDraw('Polygon'); } catch {}
        openToolbox();
        return;
      }
    
      // cambio layer: chiudi quello pendente
      if (selectedId.current && selectedId.current !== layerId) {
        abandonIfPending(map);
      }
    
      // nuova sessione
      beginEditSession(layer, layerId);
    
      // bookkeeping & UI
      setPolygons(prev => [...prev.filter(p => p.layerId !== layerId), { layerId, layer }]);
      handleRemoveButton(!!(layer._drawnByGeoman || layer._isNew));
      map.eachLayer(l => { if (l.pm && !layer.pm.enabled()) l.pm.disable(); });
      try { map.pm.disableDraw('Polygon'); } catch {}
      openToolbox();
    }
    */

    const handleCloseUploadModal = (value) => {
        setShowUploadModal(value);
    }

    const handleRemoveButton = (value) => {
        setShowRemoveButton(value);
    }

    useEffect(() => { // queste righe servono a mostrare le attribution in base al click di un button
        const attributionControl = document.querySelector('.leaflet-control-attribution'); // recupera l'elemento dal documento HTML
        if (attributionControl) { // se l'elemento esiste
            attributionControl.style.display = showAttribution ? 'block' : 'none'; // le attribution vengono mostrate (oppure no)
        }
    }, [showAttribution]); // riesegue se cambia lo stato showAttribution

    /*      
        // Aggiorna stile in base al layer (poligono) selezionato
        useEffect(() => {
            polygons.forEach(({ layerId, layer }) => { // per ogni poligono, definiti id e layer
                if (layerId === selectedId.current) { // se l'id è uguale a quello del poligono selezionato
                    layer.setStyle({ color: 'green' }); // imposta il colore verde
                } else if (layer.options.modified) { // altrimenti, se il layer è stato modificato
                    layer.setStyle({ color: 'yellow' });  // imposta il colore giallo
                } else { // altrimenti
                    layer.setStyle({ color: '#3388ff' }); // imposta il colore di default
                    //layer.pm.disableLayerDrag(); // disabilita Drag
                }
            });
        }, [selectedId.current, polygons]); // riesegue se ci sono variazioni su selectedId o polygons
    */
    const [map, setMap] = useState(null);
    useEffect(() => {
        if (!map) return;
        const id = setInterval(() => map.invalidateSize(true), 400);
        return () => clearInterval(id);
    }, [map]);

    // serve per aggiornare le parcel dopo la modifica al db --- ON PARCELS UPDATE: 
    useEffect(() => {
        if (!map) return;

        const s = editSession.current;
        if (s?.shadow) {
            // 1) riaccendi sempre il base se era stato offuscato
            try {
                if (s.baseLayer && s.baseLayer._prevStyleForEdit) {
                    s.baseLayer.setStyle(s.baseLayer._prevStyleForEdit);
                    delete s.baseLayer._prevStyleForEdit;
                }
            } catch { }

            // 2) poi rimuovi la shadow
            try { map.removeLayer(s.shadow); } catch { }

            editSession.current = null;
            activeLayer.current = null;
            selectedId.current = null;
        }

        setShowToolbox(false);
        handleRemoveButton(false);
        setIsSaved(false);

        // reset geoman
        try {
            map.pm?.disableGlobalCutMode?.();
            map.pm?.disableDraw?.('Polygon');
            map.pm?.getGeomanLayers?.()?.forEach(l => {
                l.pm?.disable?.(); l.pm?.disableLayerDrag?.(); l.pm?.disableRotate?.();
            });
        } catch { }
    }, [parcels, map]);

    function ResetViewControl({
        parcels = [],
        initialCenter = [41.892, 12.511],
        initialZoom = 12,
        title = 'Reset mappa'
    }) {
        const map = useMap();
        const containerRef = React.useRef(null);
        // calcola i bounds da tutte le parcelle visibili
        const computeBounds = React.useCallback(() => {
            const latlngs = [];
            parcels.forEach(p => {
                if (Array.isArray(p.coords) && p.coords.length) {
                    // p.coords è un ring [ [lat, lng], ... ]
                    p.coords.forEach(ll => { if (Array.isArray(ll) && ll.length === 2) latlngs.push(ll); });
                }
            });
            if (!latlngs.length) return null;
            return L.latLngBounds(latlngs);
        }, [parcels]);

        const recenter = React.useCallback(() => {
            if (!map) return;
            try {
                map.setView(initialCenter, initialZoom);
                /*const b = computeBounds();
                if (b && b.isValid()) {
                    map.fitBounds(b, { padding: [20, 20] });
                } else {
                    map.setView(initialCenter, initialZoom);
                }*/
                // chiudo eventuali modalità geoman rimaste aperte
                try {
                    map.pm?.disableGlobalCutMode?.();
                    map.pm?.disableDraw?.('Polygon');
                } catch (_) { }
            } catch (_) {
                map.setView(initialCenter, initialZoom);
            }
        }, [map, computeBounds, initialCenter, initialZoom]);

        useEffect(() => {
            if (!map) return;

            const ResetCtl = L.Control.extend({
                options: { position: 'bottomright' },
                onAdd: () => {
                    const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-reset');
                    const btn = L.DomUtil.create('a', 'leaflet-control-reset-button', container);

                    btn.innerHTML = '<span class="reset-icon" aria-hidden="true">⤾</span>';
                    btn.href = '#';
                    btn.title = title;
                    btn.setAttribute('role', 'button');
                    btn.setAttribute('aria-label', title);
                    btn.setAttribute('tabindex', '0');

                    L.DomEvent.disableClickPropagation(container);
                    L.DomEvent.disableScrollPropagation(container);

                    const safeInvoke = (e) => { L.DomEvent.stop(e); recenter(); };
                    L.DomEvent.on(btn, 'click', safeInvoke);
                    L.DomEvent.on(btn, 'keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') safeInvoke(e);
                    });

                    containerRef.current = container;
                    return container;
                }
            });

            const ctl = new ResetCtl();
            map.addControl(ctl);

            return () => {
                containerRef.current = null;
                map.removeControl(ctl);
            };
        }, [map, recenter, title]);

        // tienilo sempre "ultimo" nel corner in basso a destra (sotto la toolbox Geoman)
        useEffect(() => {
            const c = containerRef.current;
            if (!map || !c) return;
            const corner = map._controlCorners?.topright;
            if (corner) corner.appendChild(c);
        });

        return null;
    }

    const GeomanControls = () => {
        const map = useMap();
        map.doubleClickZoom.disable();

        useEffect(() => {
            if (!map.pm) return;

            map.pm.addControls({
                position: 'bottomright',
                drawMarker: false,
                drawPolygon: true,
                drawPolyline: false,
                drawCircle: false,
                drawRectangle: true,
                editMode: false,
                dragMode: false,
                cutPolygon: false,
                rotateMode: false,
                removalMode: false,
                drawText: false,
                drawCircleMarker: false,
                oneBlock: true, // mostra i controlli in un unico blocco
            });

            map.pm.setGlobalOptions({
                allowSelfIntersection: false // no intersezione tra i poligoni
            });

        }, [map]);

        return null;
    };

    function openToolbox() {
        setShowToolbox(true);
    }

    const EditPolygon = () => {
        const map = useMap();
        map.doubleClickZoom.disable();

        function editPolygon(layerId, layer) {
            map.pm.disableGlobalCutMode();
            layer.pm.disableLayerDrag();
            layer.pm.disableRotate();
            map.eachLayer(l => { // Disattiva la modifica su tutti gli altri layer
                if (l.pm) l.pm.disable();
            });
            layer.pm.enable() // Abilita la modifica solo sul layer selezionato
        }

        return (
            <Button className='polygon-edit-button' title={t("map:functions.editLayer")} onClick={() => editPolygon(selectedId.current, activeLayer.current)}>
                <i class="bi bi-pencil edit-icon" />
            </Button>
        )
    }

    const RemovePolygon = () => {
        const map = useMap();
        map.doubleClickZoom.disable();

        function removePolygon(layerId, layer) {
            map.removeLayer(layer);
            setShowToolbox(false);
        }

        return (
            <Button className='polygon-remove-button' title={t("map:functions.removeLayer")} onClick={() => removePolygon(selectedId.current, activeLayer.current)}>
                <i class="bi bi-eraser remove-icon" />
            </Button>
        )
    }

    const CreatePolygonAndToolbox = () => {
        const map = useMap();
        map.doubleClickZoom.disable();

        // --- GEOMAN CREATE
        useEffect(() => {
            if (!map?.pm) return;

            const onCreate = (e) => {
                const layer = e.layer;
                const layerId = L.Util.stamp(layer);
                if (!layer.feature) layer.feature = { type: 'Feature', properties: {} };
                layer._isNew = true; // IMPORTANTISSIMO: segnala che non esiste su React

                // seleziona subito come "ombra"
                selectPolygon(map, layer, layerId);

                // se l’utente poi modifica, colora “modified”
                layer.on('pm:update', (ev) => markModified(ev.layer));
            };

            map.on('pm:create', onCreate);
            return () => map.off('pm:create', onCreate);
        }, [map]);
    }

    const DownloadLayer = () => {
        const map = useMap();
        map.doubleClickZoom.disable();
        let layerToDownload = null;

        function downloadLayer() {
            if (activeLayer.current instanceof L.TileLayer) return;
            if (activeLayer.current.toGeoJSON) { // se il layer ha geometria
                const thisLayer = activeLayer.current.toGeoJSON();
                layerToDownload = thisLayer;
            }
            const thisGeojson = activeLayer.current.toGeoJSON();

            const blob = new Blob([JSON.stringify(thisGeojson, null, 2)], {
                type: 'application/json',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `map-layer-${Date.now()}.geojson`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }

        return (
            <Button className='download-geojson-button' title={t("map:functions.downloadLayer")} onClick={() => downloadLayer()}>
                <i class="bi bi-download download-icon" />
            </Button>
        )
    }

    // --- CLOSE BUTTON:
    const CloseToolbox = () => {
        const map = useMap();
        map.doubleClickZoom.disable();
        return (
            <Button className='close-toolbox-button-polygon' title={t("map:functions.closeToolbox")} onClick={() => abandonEdit(map)}>
                <i className="bi bi-x close-icon" />
            </Button>
        );
    };

    const SaveLayer = () => {
        const map = useMap();
        map.doubleClickZoom.disable();

        const handleSave = () => {
            const s = editSession.current;
            const shadow = s?.shadow;
            if (!shadow?.toGeoJSON) return;

            // -- Per fare in modo di gestire cancel sulla modal commentiamo try { shadow.pm?.disable?.(); } catch {}

            const feature = JSON.parse(JSON.stringify(shadow.toGeoJSON()));
            const parcelId = feature?.properties?.parcelId ?? null;
            const name = feature?.properties?.name ?? '';
            const description = feature?.properties?.description ?? '';

            // chiudi solo la UI (mantieni base spento e shadow visibile mentre decidi in modale)
            // setShowToolbox(false); 

            onSaveLayer({ parcelId, name, description, geojson: feature });
        };

        return (
            <Button className='save-modal-button-polygon' title={t("map:functions.saveLayer")} onClick={handleSave}>
                <i className="bi bi-floppy save-icon" />
            </Button>
        );
    };

    const Upload = () => {
        const map = useMap();
        map.doubleClickZoom.disable();
        const fileInputRef = useRef(null);

        function openFilePicker() {
            fileInputRef.current?.click();
        }

        // Mantengo la vecchia logica di parsing file
        const handleFileChange = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const geojsonFile = JSON.parse(event.target.result);
                    loadGeoJsonOnMap(geojsonFile);
                } catch (err) {
                    showToast(t('map:messages.errorGeoJsonFormat'), "error");
                }
            };
            reader.readAsText(file);
        };

        function loadGeoJsonOnMap(geojson) {
            L.geoJSON(geojson, {
                onEachFeature: (feature, layer) => {
                    const layerId = L.Util.stamp(layer);
                    if (!layer.feature) layer.feature = { type: "Feature", properties: {} };
                    layer._isNew = true;
                    selectPolygon(map, layer, layerId);

                    layer.setStyle({ color: "green", weight: 3, opacity: 1, fillOpacity: 0.25 });
                    layer.bringToFront?.();

                    layer.on("pm:update", (e) => {
                        markModified(e.layer);
                        e.layer.setStyle({ color: "yellow" });
                        e.layer.pm.setOptions({
                            pathOptions: { color: "yellow" },
                        });
                    });
                },
            }).addTo(map);
        }

        return (
            <>
                {/* Input file sempre nascosto, cliccato dal bottone freccetta */}
                <input
                    id="direct-upload-file-input"
                    type="file"
                    accept=".geojson,.json"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />

                {/* Bottone freccetta – ora apre direttamente il file picker */}
                <UploadControl
                    onClick={() => openFilePicker()}
                    disabled={showToolbox}
                    title={t("map:functions.createParcelFromGeoJSON")}
                />
            </>
        );
    };

    const ReplaceGeometryFromGeoJSON = () => {
        const map = useMap();
        const fileInputRef = useRef(null);

        const lonLatToLatLng = ([lng, lat]) => [lat, lng];

        const pickGeometry = (g) => {
            if (!g) return null;
            if (g.type === 'FeatureCollection') return g.features?.[0]?.geometry || null;
            if (g.type === 'Feature') return g.geometry || null;
            // se è una geometry pura:
            if (g.type && g.coordinates) return g;
            return null;
        };

        const extractOuterRingLatLng = (g) => {
            const geom = pickGeometry(g);
            if (!geom) return null;

            if (geom.type === 'Polygon') {
                const outer = geom.coordinates?.[0];
                return Array.isArray(outer) ? outer.map(lonLatToLatLng) : null;
            }
            if (geom.type === 'MultiPolygon') {
                const firstOuter = geom.coordinates?.[0]?.[0];
                return Array.isArray(firstOuter) ? firstOuter.map(lonLatToLatLng) : null;
            }
            return null; // altri tipi: no
        };

        const onChooseFile = () => fileInputRef.current?.click();

        const onFileChange = (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (ev) => {
                try {
                    const geojson = JSON.parse(ev.target.result);
                    const latlngs = extractOuterRingLatLng(geojson);
                    const layer = activeLayer.current;

                    if (!layer || !layer.setLatLngs || !latlngs?.length) {
                        return;
                    }
                    // Applica la nuova geometria al layer selezionato
                    layer.setLatLngs(latlngs);
                    // evidenzia “modificato” 
                    markModified(layer);
                    layer.setStyle({ color: 'yellow' });
                    layer.pm?.setOptions?.({ pathOptions: { color: 'yellow' } });
                    // lasciamo l’utente premere "Save" per confermare, oppure "X" per annullare (ripristino)

                } catch (err) {
                    // Dopo ci devo mettere uno showtoast qui. Ma non l'hanno usato fino a ora si deve rivedere il tutto
                    showToast(t('map:messages.errorGeoJsonFormat'), "error");
                } finally {
                    // reset input per permettere lo stesso file di nuovo, se serve
                    e.target.value = '';
                }
            };
            reader.readAsText(file);
        };

        return (
            <>
                <input
                    type="file"
                    accept=".geojson,.json"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={onFileChange}
                />
                <Button className={showRemoveButton ? 'polygon-replace-geojson-button' : 'polygon-replace-geojson-button2'} title={t("map:functions.replaceGeometryFromGeoJSON")} onClick={onChooseFile}>
                    <i class="bi bi-braces geojson-icon" />
                </Button>
            </>
        );
    };

    function UploadControl({ onClick, disabled: disabledProp = false, title = t("map:label.createParcelFromGeoJSON") }) {
        const map = useMap();
        const [isDrawing, setIsDrawing] = React.useState(false);
        const containerRef = React.useRef(null); //  salva il DOM del control
        // 1) Blocco la creazione del controllo ad una sola volta
        useEffect(() => {
            if (!map) return;

            const UploadCtl = L.Control.extend({
                options: { position: 'bottomright' },
                onAdd: () => {
                    const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-upload');
                    const btn = L.DomUtil.create('a', 'leaflet-control-upload-button', container);

                    btn.innerHTML = '<span class="upload-icon" aria-hidden="true">⬆️</span>';
                    btn.href = '#';
                    btn.title = title;
                    btn.setAttribute('role', 'button');
                    btn.setAttribute('aria-label', title);
                    btn.setAttribute('tabindex', '0');

                    L.DomEvent.disableClickPropagation(container);
                    L.DomEvent.disableScrollPropagation(container);

                    const safeInvoke = (e) => {
                        L.DomEvent.stop(e);
                        if (!(disabledProp || isDrawing)) onClick?.();
                    };

                    L.DomEvent.on(btn, 'click', safeInvoke);
                    L.DomEvent.on(btn, 'keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') safeInvoke(e);
                    });

                    container._btn = btn;
                    containerRef.current = container;  // <-- memorizza
                    return container;
                }
            });

            const ctl = new UploadCtl();
            map.addControl(ctl);

            return () => {
                containerRef.current = null;
                map.removeControl(ctl);
            };
        }, [map, onClick, title]);

        // 2) Catturo los tato di draw mode di Geoman (senza rimontare il control)
        useEffect(() => {
            if (!map || !map.pm) return;

            const init = (() => {
                try {
                    if (typeof map.pm.globalDrawModeEnabled === 'function') return !!map.pm.globalDrawModeEnabled();
                    return !!map.pm.globalDrawMode;
                } catch { return false; }
            })();
            setIsDrawing(init);
            const onToggle = (e) => setIsDrawing(!!e.enabled);
            map.on('pm:globaldrawmodetoggled', onToggle);
            return () => map.off('pm:globaldrawmodetoggled', onToggle);
        }, [map]);

        // 3) quando cambia disabled/draw mode aggiorna classi e lo mantengo in fondo allo stack
        useEffect(() => {
            const container = containerRef.current;
            if (!map || !container) return;

            const corner = map._controlCorners?.topright; // div del corner
            if (corner) {
                // ri-appendi per tenerlo sempre “ultimo” (in basso)
                corner.appendChild(container);
            }
            const effectiveDisabled = disabledProp || isDrawing;
            if (effectiveDisabled) {
                container.classList.add('is-disabled');
                container._btn?.setAttribute('aria-disabled', 'true');
            } else {
                container.classList.remove('is-disabled');
                container._btn?.removeAttribute('aria-disabled');
            }
        }, [map, disabledProp, isDrawing]);

        return null;
    }

    function ZoomControlledPolygon({ parcel }) {
        const map = useMap();
        map.doubleClickZoom.disable();
        const tooltipThreshold = 7;
        const [showPolygon, setShowPolygon] = useState(map.getZoom() >= tooltipThreshold);

        useMapEvents({
            zoomend: () => {
                const zoom = map.getZoom();
                setShowPolygon(zoom >= tooltipThreshold);
            }
        });

        return showPolygon && (
            <Polygon
                positions={parcel.coords}
                eventHandlers={{ // gestione degli eventi
                    add: (e) => {
                        const layer = e.target;
                        // assicura un wrapper Feature standard
                        if (!layer.feature) layer.feature = { type: 'Feature', properties: {} };
                        layer.feature.properties.parcelId = parcel.parcelId;
                        layer.feature.properties.name = parcel.name ?? '';
                        layer.feature.properties.description = parcel.description ?? '';
                    },
                    click: (e) => { // al click sul poligono (parcel)
                        const layer = e.target;                // questo è il layer React
                        const layerId = e.target._leaflet_id;  // id solo per i tuoi stili
                        selectPolygon(e.target._map, layer, layerId);
                    }
                }}
            >
                <MapLabel position={parcel.centroid} labelThreshold={11}>
                    <div className='div-label-parcel-name'>{parcel.name}</div>
                </MapLabel>
            </Polygon>
        );
    }

    return (
        <MapContainer id='tab-map' center={[41.892, 12.511]} zoom={6} scrollWheelZoom={true} ref={setMap}>
            <Button className='attribution-button' onClick={() => setShowAttribution(!showAttribution)}>
                <i title={t("map:functions.showAttribution")} className='bi bi-info attribution-icon' />
            </Button>
            {showToolbox && (
                <>
                    <ButtonGroup className='polygon-toolbox-button-group'>
                        {showRemoveButton && <RemovePolygon />}
                        <ReplaceGeometryFromGeoJSON />
                        <DownloadLayer />
                        <SaveLayer onSaveLayer={onSaveLayer} />
                        <CloseToolbox />
                    </ButtonGroup>
                </>
            )}
            <Upload />
            <CreatePolygonAndToolbox />
            <ResetViewControl
                parcels={parcels}
                initialCenter={[41.892, 12.511]}
                initialZoom={6}
                title={t("map:functions.resetZoom")}
            />
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MarkerClusterGroup
                eventHandlers={{
                    clusterclick: (e) => { console.log(e); }
                }}
            >
                {parcels && parcels.map((parcel) => { // ciclo sulle parcel della lista "parcels"
                    return (
                        <React.Fragment key={parcel.parcelId}>
                            <ZoomControlledPolygon parcel={parcel} />
                            <Marker /* la posizione del marker di info e monitoring della parcel corrisponde al centroide del poligono */
                                key={parcel.parcelId}
                                position={parcel.centroid}
                                icon={parcel.parcelId === clickedInfo ? infoParcelIconActive : infoParcelIcon} /* se viene cliccata l'icona 
                                        del marker, cambia colore; viene utilizzato l'id della parcel per distinguere le singole icone: se l'id della 
                                        parcel relativo all'icona che su cui si è cliccato corrisponde a quello della parcel attiva, l'icona cambia colore */
                                eventHandlers={{ // gestione degli eventi
                                    click: () => {
                                        setShowMonitoring(true);
                                    },
                                    popupopen: () => { // all'apertura del popup
                                        setClickedInfo(parcel.parcelId); // cambia lo stato "clicked" e quindi il colore dell'icona
                                    },
                                    popupclose: () => { // alla chiusura del popup
                                        setClickedInfo(null); // l'icona torna al colore originale
                                        setShowMonitoring(false);
                                    }
                                }}
                            >
                                <Popup className='parcel-popup'>
                                    <Row>
                                        <Col className='col-info'>
                                            <Container className='container-parcel-popup' fluid={true}>
                                                <Card className='parcel-popup-icon-card'>
                                                    <Card.Body className='parcel-popup-icon-card-body'>
                                                        <img className='parcel-popup-icon' src='polygon.svg' width='40' height='40' />
                                                    </Card.Body>
                                                </Card>
                                                <Row className='parcel-name-row'>
                                                    <Col>
                                                        <span className='parcel-name-span'>
                                                            {parcel.name}
                                                        </span>
                                                    </Col>
                                                </Row>
                                                <Row className='soil-type-row'>
                                                    <Row className='soil-type-row-header'>
                                                        <Col>
                                                            <div className='soil-type-div'>
                                                                {t("map:label.soilType")}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className='soil-type-row-value'>
                                                        <Col>
                                                            <Card className='soil-type-card'>
                                                                <Card.Body className='soil-type-card-body'>
                                                                    {parcel.soilData.soilType}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Row>
                                                <Row className='sand-silt-clay-row'>
                                                    <Row className='sand-silt-clay-row-header'>
                                                        <Col>
                                                            <div className='parcel-sand-div'>
                                                                {t("map:label.sand")}
                                                            </div>
                                                        </Col>
                                                        <Col>
                                                            <div className='parcel-silt-div'>
                                                                {t("map:label.silt")}
                                                            </div>
                                                        </Col>
                                                        <Col>
                                                            <div className='parcel-clay-div'>
                                                                {t("map:label.clay")}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className='sand-silt-clay-row-values'>
                                                        <Col>
                                                            <Card className='parcel-sand-card'>
                                                                <Card.Body className='parcel-sand-card-body'>
                                                                    {parcel.soilData.sandPercentage}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col>
                                                            <Card className='parcel-silt-card'>
                                                                <Card.Body className='parcel-silt-card-body'>
                                                                    {parcel.soilData.siltPercentage}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col>
                                                            <Card className='parcel-clay-card'>
                                                                <Card.Body className='parcel-clay-card-body'>
                                                                    {parcel.soilData.clayPercentage}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Row>
                                                <Row className='texture-row'>
                                                    <Row className='texture-row-header'>
                                                        <Col>
                                                            <div className='texture-div'>
                                                                {t("map:label.texture")}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className='texture-row-value'>
                                                        <Col>
                                                            <Card className='texture-card'>
                                                                <Card.Body className='texture-card-body'>
                                                                    {parcel.soilData.texture}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Row>
                                                <Row className='organic-matter-ph-row'>
                                                    <Row className='organic-matter-ph-row-header'>
                                                        <Col>
                                                            <div className='organic-matter-div'>
                                                                {t("map:label.organicMatter")}
                                                            </div>
                                                        </Col>
                                                        <Col sm={3}>
                                                            <div className='ph-div'>
                                                                Ph
                                                            </div>
                                                        </Col>
                                                        <Col sm={4}>
                                                            <div className='area-div'>
                                                                {t("map:label.area")}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className='organic-matter-ph-row-values'>
                                                        <Col>
                                                            <Card className='organic-matter-card'>
                                                                <Card.Body className='organic-matter-card-body'>
                                                                    {parcel.soilData.organicMatter}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col sm={3}>
                                                            <Card className='ph-card'>
                                                                <Card.Body className='ph-card-body'>
                                                                    {parcel.soilData.ph}
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col sm={4}>
                                                            <Card className='area-card'>
                                                                <Card.Body className='area-card-body'>
                                                                    {parcel.area} Km&sup2;
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Row>
                                            </Container>
                                        </Col>
                                    </Row>
                                </Popup>
                            </Marker>
                        </React.Fragment>
                    )
                })}
            </MarkerClusterGroup>
            <GeomanControls />
        </MapContainer>
    )
}